/**
 * YardiMailboxDrawer — Add or Edit Yardi mailbox credentials.
 *
 * Second setup step for the Yardi integration (vendor_id 7). After the base
 * integration is configured (Current Integrations), this drawer captures the
 * mailbox routing fields used to exchange messages with Yardi.
 *
 * Fields (all standard text inputs except the masked password):
 *   Sending Application, Sending Facility, Receiving Application,
 *   Receiving Facility, Mailbox Password
 *
 * Props:
 *   open         — boolean
 *   onClose      — () => void
 *   onSave       — (formData) => void   formData: { sendingApplication, sendingFacility,
 *                                        receivingApplication, receivingFacility, mailboxPassword }
 *   saving       — boolean
 *   mode         — 'add' | 'edit'
 *   mailboxData  — existing mailbox record (edit mode) or null
 *   vendorName   — vendor display name for the subtitle
 */

import { useState, useEffect, useRef } from 'react';
import Drawer from '../../../../../components/v2/Drawer.jsx';

const YardiMailboxDrawer = ({ open, onClose, onSave, saving, mode = 'add', mailboxData, vendorName }) => {
  const [sendingApplication, setSendingApplication] = useState('');
  const [sendingFacility, setSendingFacility] = useState('');
  const [receivingApplication, setReceivingApplication] = useState('');
  const [receivingFacility, setReceivingFacility] = useState('');
  const [mailboxPassword, setMailboxPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const firstInputRef = useRef(null);

  // Reset form when drawer opens / record changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && mailboxData) {
        setSendingApplication(mailboxData.sending_application || '');
        setSendingFacility(mailboxData.sending_facility || '');
        setReceivingApplication(mailboxData.receiving_application || '');
        setReceivingFacility(mailboxData.receiving_facility || '');
        setMailboxPassword(mailboxData.mailbox_password || '');
      } else {
        setSendingApplication('');
        setSendingFacility('');
        setReceivingApplication('');
        setReceivingFacility('');
        setMailboxPassword('');
      }
      setShowPassword(false);
      setErrors({});
      setTimeout(() => firstInputRef.current?.focus(), 150);
    }
  }, [open, mode, mailboxData]);

  // ── Validation ──────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!sendingApplication.trim()) errs.sendingApplication = 'Sending Application is required.';
    if (!sendingFacility.trim()) errs.sendingFacility = 'Sending Facility is required.';
    if (!receivingApplication.trim()) errs.receivingApplication = 'Receiving Application is required.';
    if (!receivingFacility.trim()) errs.receivingFacility = 'Receiving Facility is required.';
    if (!mailboxPassword.trim()) errs.mailboxPassword = 'Mailbox Password is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      sendingApplication: sendingApplication.trim(),
      sendingFacility: sendingFacility.trim(),
      receivingApplication: receivingApplication.trim(),
      receivingFacility: receivingFacility.trim(),
      mailboxPassword: mailboxPassword.trim(),
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Drawer
        open={open}
        onClose={onClose}
        title={mode === 'add' ? 'Add Mailbox Credentials' : 'Edit Mailbox Credentials'}
        backdrop={false}
        ariaLabel={mode === 'add' ? 'Add Mailbox Credentials' : 'Edit Mailbox Credentials'}
        bodyAs="form"
        bodyProps={{ onSubmit: handleSubmit, noValidate: true }}
        footer={(
            <>
                <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
                  {saving ? 'Saving...' : mode === 'add' ? 'Add Credentials' : 'Save'}
                </button>
            </>
        )}
        subheader={(
            <>
                {vendorName && (
                        <div style={{ padding: '0 1.5rem 0.5rem', color: 'var(--color-text-secondary, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)', fontWeight: 'var(--font-weight-medium, 500)' }}>
                          {vendorName}
                        </div>
                      )}
            </>
        )}
    >
        {/* Sending Application */}
        <div className="dp-drawer__field">
          <label htmlFor="ymb-sending-app" className="dp-drawer__label">Sending Application *</label>
          <input
            ref={firstInputRef}
            id="ymb-sending-app"
            type="text"
            className={`dp-drawer__input${errors.sendingApplication ? ' is-invalid' : ''}`}
            value={sendingApplication}
            onChange={(e) => setSendingApplication(e.target.value)}
            placeholder="Enter sending application"
            disabled={saving}
          />
          {errors.sendingApplication && <span className="dp-drawer__error">{errors.sendingApplication}</span>}
        </div>

        {/* Sending Facility */}
        <div className="dp-drawer__field">
          <label htmlFor="ymb-sending-facility" className="dp-drawer__label">Sending Facility *</label>
          <input
            id="ymb-sending-facility"
            type="text"
            className={`dp-drawer__input${errors.sendingFacility ? ' is-invalid' : ''}`}
            value={sendingFacility}
            onChange={(e) => setSendingFacility(e.target.value)}
            placeholder="Enter sending facility"
            disabled={saving}
          />
          {errors.sendingFacility && <span className="dp-drawer__error">{errors.sendingFacility}</span>}
        </div>

        {/* Receiving Application */}
        <div className="dp-drawer__field">
          <label htmlFor="ymb-receiving-app" className="dp-drawer__label">Receiving Application *</label>
          <input
            id="ymb-receiving-app"
            type="text"
            className={`dp-drawer__input${errors.receivingApplication ? ' is-invalid' : ''}`}
            value={receivingApplication}
            onChange={(e) => setReceivingApplication(e.target.value)}
            placeholder="Enter receiving application"
            disabled={saving}
          />
          {errors.receivingApplication && <span className="dp-drawer__error">{errors.receivingApplication}</span>}
        </div>

        {/* Receiving Facility */}
        <div className="dp-drawer__field">
          <label htmlFor="ymb-receiving-facility" className="dp-drawer__label">Receiving Facility *</label>
          <input
            id="ymb-receiving-facility"
            type="text"
            className={`dp-drawer__input${errors.receivingFacility ? ' is-invalid' : ''}`}
            value={receivingFacility}
            onChange={(e) => setReceivingFacility(e.target.value)}
            placeholder="Enter receiving facility"
            disabled={saving}
          />
          {errors.receivingFacility && <span className="dp-drawer__error">{errors.receivingFacility}</span>}
        </div>

        {/* Mailbox Password (masked) */}
        <div className="dp-drawer__field">
          <label htmlFor="ymb-password" className="dp-drawer__label">Mailbox Password *</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              id="ymb-password"
              type={showPassword ? 'text' : 'password'}
              className={`dp-drawer__input${errors.mailboxPassword ? ' is-invalid' : ''}`}
              value={mailboxPassword}
              onChange={(e) => setMailboxPassword(e.target.value)}
              placeholder="Enter mailbox password"
              autoComplete="new-password"
              disabled={saving}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="dp-btn dp-btn--ghost dp-btn--sm cpp-reveal-btn"
              onClick={() => setShowPassword((prev) => !prev)}
              title={showPassword ? 'Hide password' : 'Show password'}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              )}
            </button>
          </div>
          {errors.mailboxPassword && <span className="dp-drawer__error">{errors.mailboxPassword}</span>}
        </div>

        {/* Footer */}
    </Drawer>
  );
};

export default YardiMailboxDrawer;
